<template>
  <form v-if="value" @submit.prevent="onSubmit">
    <p>
      Vous êtes sur le point de générer les déclinaisons pour le produit <strong>{{value.name}}&nbsp;?</strong>
    </p>

    <row>
      <column v-for="property in productPropertiesToChoose" :key="property.id" sm="4">
        <form-group :label="property.name" :required="property.required">
          <form-multiselect v-model="variantsOptions[property.id]" :options="getPropertyOptions(property)" select-all multiple />
        </form-group>
      </column>
    </row>

    <p v-if="invalidVariants.length">
      <form-checkbox v-model="deleteInvalidVariants"> Supprimer les {{invalidVariants.length}} déclinaisons invalides</form-checkbox>
    </p>
    <p v-if="oldVariants.length">
      <form-checkbox v-model="deleteOldVariants"> Supprimer les {{oldVariants.length}} anciennes déclinaisons</form-checkbox>
    </p>

    <p>Avec cette configuration, le produit contiendra au total <strong>{{totalVariantsCount + (deleteInvalidVariants? 0: invalidVariants.length) + (deleteOldVariants? 0: oldVariants.length)}} déclinaison(s)</strong>.</p>

    <form-button :kind="newVariantsCount > 0? 'submit': 'delete'" type="submit">
       Mettre à jour
    </form-button>
    <form-button kind="cancel" @click="onCancel">Annuler</form-button>
  </form>
</template>

<script>
import _mapValues from "lodash/mapValues";
import _map from "lodash/map";
import _some from "lodash/some";
import _keyBy from "lodash/keyBy";
import _pick from "lodash/pick";
import _isEqual from "lodash/isEqual";
import _flatten from "lodash/flatten";

import {ProductPropertySexOptionsArray} from '../../common/types';

export default {
  components: {
    FormMultiselect: () =>
      import("package-common-vuejs/src/components/form/FormMultiselect")
  },
  props: {
    value: Object
  },
  data() {
    return {
      variantsOptions: {},
      deleteOldVariants: false,
      deleteInvalidVariants: true,
    };
  },
  computed: {
    invalidVariants() {
      const properties = this.productPropertiesToChoose;
      return (this.value && this.value.variants || [])
        .filter(({attributes}) => {
          const propertiesWithValidValue = properties.filter(({id, kind, required, colors, tags}) => {
            const attribute = (attributes || []).find(({property}) => property === id);
            if (!attribute) return false; // Aucun attribut pour cette propriété
            if (!attribute.value) return !required; // Aucune valeur pour l'attribut obligatoire
            switch (kind) {
              case 'color':
                return !!colors.find(({id}) => id === attribute.value);
              case 'tag':
                return tags.indexOf(attribute.value) !== -1;
              case 'sex':
                return ProductPropertySexOptionsArray.find(({value}) => attribute.value === value);
            }
            return false;
          });
          return propertiesWithValidValue.length !== properties.length;
        });
    },
    oldVariants() {
      const propertiesToFind = _map(this.productPropertiesToChoose, 'id');
      const attributesToFindObject = this.variantsOptions;
      const invalidVariants = _map(this.invalidVariants, 'id');
      return (this.value && this.value.variants || [])
        .filter(({attributes}) => {
          const attributesObject = _pick(_mapValues(_keyBy(attributes, 'property'), 'value'), propertiesToFind);
          const hasNoAttributeValueSelected = _some(_map(attributesObject, (value, prop) => (attributesToFindObject[prop] || ['']).indexOf(value) === -1));
          return hasNoAttributeValueSelected;
        })
        .filter(({id}) => invalidVariants.indexOf(id) === -1);
    },
    productPropertiesToChoose() {
      return ((this.value && this.value.properties) || []).filter(({kind}) => kind !== 'text');
    },
    totalVariantsCount() {
      const values = _map(this.productPropertiesToChoose, ({id, required}) => (this.variantsOptions[id] || (required? []: [''])).length);
      const total = values.reduce((total, multiple) => total * multiple, 1);
      return total;
    },
    existingVariantsCount() {
      return this.value.variants.length;
    },
    newVariantsCount() {
      return this.totalVariantsCount - this.existingVariantsCount + this.invalidVariants.length;
    }
  },
  methods: {
    getPropertyOptions(property) {
      switch (property.kind) {
        case 'color':
          return _map(property.colors, ({id, name}) => ({value: id, label: name}));
        case 'tag':
          return _map(property.tags, tag => ({value: tag, label: tag}));
        case 'sex':
          return ProductPropertySexOptionsArray;
      }
      return [];
    },
    getVariantWithAttributes(attributesToFind) {
      const attributesToFindObject = _mapValues(_keyBy(attributesToFind, 'property'), 'value');
      const propertiesToFind = Object.keys(attributesToFindObject);
      return (this.value && this.value.variants || []).find(({attributes}) => {
        const attributesObject = _pick(_mapValues(_keyBy(attributes, 'property'), 'value'), propertiesToFind);
        return _isEqual(attributesObject, attributesToFindObject);
      });
    },
    onSubmit() {
      let attributesSet = [[]]; // All attributes set list
      this.productPropertiesToChoose.forEach(property => {
        const valuesSelected = this.variantsOptions[property.id];
        const values = property.required? valuesSelected: (valuesSelected && valuesSelected.length? valuesSelected: ['']);
        attributesSet = _flatten(_map(values, value => {
          return attributesSet.map(attributes => ([
            ...attributes,
            {property: property.id, value},
          ]));
        }));
      });
      const variants = [
        ...attributesSet.map(attributes => {
          return this.getVariantWithAttributes(attributes) || {
            reference: '',
            published: false,
            attributes,
          };
        }),
        ...(this.deleteOldVariants? []: this.oldVariants),
        ...(this.deleteInvalidVariants? []: this.invalidVariants),
      ];
      this.$emit("submit", variants);
    },
    onCancel() {
      this.$emit("cancel");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/products/VariantsGeneratorForm.vue